import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';

/**
 * Test a trunk, or register it now, and see what the carrier said (owner, 26 September 2026: "are
 * we passing back any sip messages? digest auth messages? something we could use to
 * troubleshoot"). The switch runs it (arc-agent trunktest.ts); this asks and shows the answer:
 * each step, and under it the SIP messages as they went, the digest response hidden.
 */

interface SipMessage { dir: 'sent' | 'received'; at: string; text: string }
interface Step { key: string; title: string; ok: boolean | null; detail: string; messages?: SipMessage[] }
interface TestRun {
  id: string; action: 'test' | 'register'; status: 'queued' | 'running' | 'done' | 'abandoned';
  requested_at: string; finished_at: string | null;
  result: { ok: boolean; summary: string; steps: Step[] } | null;
}

const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export function TrunkTest({ trunkId, tenantId, authMode, autoRun }: {
  trunkId: string; tenantId?: string | undefined; authMode: string;
  /** Run the test as soon as it is shown: the set-up wizard, when a new trunk did not register. */
  autoRun?: boolean;
}) {
  const qc = useQueryClient();
  const base = tenantId ? `/v1/tenants/${tenantId}/own-trunks/${trunkId}/tests` : `/v1/trunks/${trunkId}/tests`;
  const [runId, setRunId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: (action: 'test' | 'register') => api.post<{ id: string }>(base, { action }),
    onSuccess: (r) => setRunId(r.id),
  });
  const run = useQuery({
    queryKey: ['trunk-test', trunkId, runId],
    queryFn: () => api.get<TestRun>(`${base}/${runId}`),
    enabled: runId !== null,
    // Every second until the switch has answered.
    refetchInterval: (q) => (q.state.data?.status === 'done' || q.state.data?.status === 'abandoned' ? false : 1_000),
    refetchIntervalInBackground: true,
  });
  useEffect(() => {
    if (autoRun) start.mutate('test');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun]);
  const r = run.data;
  const busy = start.isPending || (runId !== null && (!r || r.status === 'queued' || r.status === 'running'));

  // A registration forced now shows in the live panel above once it has settled.
  useEffect(() => {
    if (r?.status === 'done') void qc.invalidateQueries({ predicate: (q) => q.queryKey.some((k) => typeof k === 'string' && /trunk/i.test(k)) });
  }, [r?.status, qc]);

  return (
    <div style={{ marginTop: 12 }}>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="arc-btn arc-btn-sm" disabled={busy} onClick={() => start.mutate('test')}
          title="Checks the carrier answers, that it accepts the username and password, and the registration">
          Test
        </button>
        {authMode === 'register' && (
          <button type="button" className="arc-btn arc-btn-sm" disabled={busy} onClick={() => start.mutate('register')}
            title="Registers with the carrier now instead of at the next renewal">
            Register now
          </button>
        )}
        {busy && <span className="arc-subtle" style={{ fontSize: 12 }}>
          {r?.status === 'running' ? 'Talking to the carrier…' : 'Waiting for the switch…'}
        </span>}
        {r?.status === 'abandoned' && <span className="arc-subtle" style={{ fontSize: 12 }}>The switch did not pick the test up. Try again in a minute.</span>}
      </div>
      {start.error && <ErrorBanner error={start.error} />}
      {run.error && <ErrorBanner error={run.error} />}

      {r?.result && (
        <div className="arc-stack" style={{ gap: 8, marginTop: 10 }}>
          <div className={`arc-banner ${r.result.ok ? 'arc-banner-info' : 'arc-banner-danger'}`} role="status">
            <Icon name={r.result.ok ? 'check' : 'warning'} size={14} />
            <span style={{ flex: 1 }}>{r.result.summary}</span>
            <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{r.finished_at ? time(r.finished_at) : ''}</span>
          </div>
          {r.result.steps.map((s, i) => (
            <div key={`${s.key}-${i}`} className="arc-stack" style={{ gap: 4, padding: '8px 10px', border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)' }}>
              <div className="arc-row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: s.ok === true ? 'var(--arc-ok)' : s.ok === false ? 'var(--arc-danger)' : 'var(--arc-fg-3)', marginTop: 2 }}>
                  <Icon name={s.ok === true ? 'check' : s.ok === false ? 'close' : 'info'} size={13} />
                </span>
                <div className="arc-stack" style={{ gap: 2, flex: 1 }}>
                  <strong style={{ fontSize: 12.5 }}>{s.title}</strong>
                  <span className="arc-muted" style={{ fontSize: 12.5 }}>{s.detail}</span>
                </div>
              </div>
              {s.messages && s.messages.length > 0 && (
                <details style={{ marginLeft: 21 }}>
                  <summary className="arc-subtle" style={{ fontSize: 12, cursor: 'pointer' }}>
                    SIP messages ({s.messages.length})
                  </summary>
                  {s.messages.map((m, j) => (
                    <div key={j} style={{ marginTop: 6 }}>
                      <div className="arc-subtle arc-num" style={{ fontSize: 11 }}>
                        {m.dir === 'sent' ? 'Sent' : 'Received'} {time(m.at)}
                      </div>
                      <pre className="arc-mono" style={{
                        margin: '2px 0 0', padding: 8, fontSize: 11.5, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        background: 'var(--arc-surface-2)', borderRadius: 'var(--arc-r-sm)',
                        borderLeft: `3px solid ${m.dir === 'sent' ? 'var(--arc-accent)' : 'var(--arc-border-strong)'}`,
                      }}>{m.text}</pre>
                    </div>
                  ))}
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
